import { useParams, Navigate, Link } from "react-router-dom";
import { PRODUCTS } from "@/data/products";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { ArrowRight, CheckCircle2, XCircle, Sparkles } from "lucide-react";
import LeadForm from "@/components/LeadForm";

export default function ProductPage() {
  const { slug } = useParams();
  const product = PRODUCTS[slug];
  if (!product) return <Navigate to="/services" replace />;

  return (
    <div className="bg-white pt-24 sm:pt-28" data-testid={`page-product-${slug}`}>
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Badge variant="outline" className="border-blue-200 text-blue-900 bg-blue-50/60">{product.name}</Badge>
            <h1 className="font-display mt-4 text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.1]">
              {product.hero}
            </h1>
            <p className="mt-5 text-slate-600 leading-relaxed text-lg max-w-xl">{product.sub}</p>
            <div className="mt-7 flex gap-3">
              <Link to="/contact">
                <Button data-testid={`product-${slug}-cta-quote`} className="bg-emerald-500 hover:bg-emerald-600 text-white">
                  Get Free Quote <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
              <Link to="/assessment">
                <Button data-testid={`product-${slug}-cta-review`} variant="outline" className="border-blue-900 text-blue-900 hover:bg-blue-50">
                  Review my existing cover
                </Button>
              </Link>
            </div>
          </div>
          <img src={product.image} alt={product.name} className="rounded-3xl soft-shadow-lg w-full h-72 lg:h-96 object-cover" />
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold text-slate-900">What is {product.name}?</h2>
          <p className="mt-4 text-slate-600 leading-relaxed">{product.whatIs}</p>

          <h3 className="font-display mt-12 text-xl font-semibold text-slate-900">Who needs it?</h3>
          <ul className="mt-4 space-y-2.5">
            {product.whoNeeds.map((w) => (
              <li key={w} className="flex items-start gap-2.5 text-slate-700">
                <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
                <span>{w}</span>
              </li>
            ))}
          </ul>

          <h3 className="font-display mt-12 text-xl font-semibold text-slate-900">Why it matters</h3>
          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            {product.benefits.map((b) => (
              <div key={b} className="rounded-xl bg-slate-50/80 border border-slate-100 p-4 text-sm text-slate-700 leading-relaxed flex gap-2.5">
                <Sparkles className="h-4 w-4 text-blue-900 mt-0.5 shrink-0" />
                <span>{b}</span>
              </div>
            ))}
          </div>

          <h3 className="font-display mt-12 text-xl font-semibold text-slate-900">Common myths, busted</h3>
          <div className="mt-4 space-y-4">
            {product.myths.map((m) => (
              <div key={m.m} className="rounded-2xl border border-slate-100 p-5 soft-shadow bg-white">
                <div className="flex gap-2.5">
                  <XCircle className="h-4 w-4 text-rose-500 mt-0.5 shrink-0" />
                  <div className="text-sm font-semibold text-slate-900">Myth: {m.m}</div>
                </div>
                <div className="mt-2 flex gap-2.5">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                  <div className="text-sm text-slate-700 leading-relaxed">Truth: {m.t}</div>
                </div>
              </div>
            ))}
          </div>

          {product.claimExamples?.length ? (
            <>
              <h3 className="font-display mt-12 text-xl font-semibold text-slate-900">Real claim examples</h3>
              <ul className="mt-4 space-y-2.5">
                {product.claimExamples.map((c) => (
                  <li key={c} className="rounded-xl bg-emerald-50/60 border border-emerald-100 p-4 text-sm text-emerald-900">
                    {c}
                  </li>
                ))}
              </ul>
            </>
          ) : null}

          <h3 className="font-display mt-12 text-xl font-semibold text-slate-900">Frequently asked</h3>
          <Accordion type="single" collapsible className="mt-4">
            {product.faqs.map((f, i) => (
              <AccordionItem key={f.q} value={`item-${i}`} data-testid={`product-${slug}-faq-${i}`}>
                <AccordionTrigger className="text-left">{f.q}</AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        <aside className="lg:sticky lg:top-28 self-start">
          <LeadForm
            title={`Get a ${product.name} quote`}
            subtitle="Compared across 30+ insurers. No spam, no pushy calls."
            source={`product_${slug}`}
            defaultInterest={product.name}
            testIdPrefix={`product-${slug}`}
          />
        </aside>
      </section>
    </div>
  );
}
